// components/ClientsTable.jsx
import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import EventIcon from "@mui/icons-material/Event";
import HistoryIcon from "@mui/icons-material/History";
import SatelliteAltIcon from "@mui/icons-material/SatelliteAlt";
import AddIcon from "@mui/icons-material/Add";
import EquiposChips from "./EquiposChips";
import ServicioAvatar from "../../../components/ServicioAvatar";
import FormularioOT from "../../ordenDeTrabajo/components/FormularioOT";
import { getPanelGlobalDetalle } from "../../panelGlobal/services/panelGlobalService";

// Mismo mapeo que ClientePlataformaVehiculos.jsx (Historial de Cliente).
const PLATAFORMA_LABEL = {
  wialon: { label: "Wialon", color: "info" },
  leasecontrol: { label: "Leasecontrol", color: "secondary" },
  trsrastreo: { label: "TRS Rastreo", color: "success" },
};

export default function ClientsTable({
  clientes,
  equiposMap,
  onEmitirReq,
  onOpenHistorial,
  onOpenPlataformas,
  page = 0,
  rowsPerPage = 30,
  total = 0,
  onPageChange,
  onRowsPerPageChange,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  // Mismo diálogo que ya usa TablaClientesOT.jsx (módulo Órdenes de Trabajo) — se abre
  // acá también para poder emitir la OT sin salir de Requerimientos, con el mismo `cli`
  // que ya trae useClientesYEquipos (mismo hook que usa ese otro módulo).
  const [otDialogOpen, setOtDialogOpen] = useState(false);
  const [clienteParaOT, setClienteParaOT] = useState(null);

  // Plataforma de rastreo (Wialon/Leasecontrol/TRS Rastreo) por cliente — mismo dato
  // que ya muestra ClientePlataformaVehiculos.jsx en el Historial de Cliente, acá
  // resumido en una columna. Solo aplica a AVL (es un dato de módems/vehículos), así
  // que se pide solo para esos clientes de la página actual, en paralelo.
  const [plataformaPorServicio, setPlataformaPorServicio] = useState({});
  useEffect(() => {
    const avlSinCargar = (clientes || []).filter(
      (c) => c.nombre_servicio === "AVL" && !(c.cod_servicio_cliente in plataformaPorServicio)
    );
    if (!avlSinCargar.length) return;
    let activo = true;
    Promise.all(
      avlSinCargar.map((c) =>
        getPanelGlobalDetalle(c.cod_servicio_cliente)
          .then((res) => [c.cod_servicio_cliente, res?.data?.plataforma || null])
          .catch(() => [c.cod_servicio_cliente, null])
      )
    ).then((pares) => {
      if (!activo) return;
      setPlataformaPorServicio((prev) => ({ ...prev, ...Object.fromEntries(pares) }));
    });
    return () => { activo = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientes]);

  // La tabla scrollea horizontal (columnas de ancho fijo) y "Acciones" quedaba fuera de
  // vista sin desplazar — se fija a la derecha, igual que un panel congelado. Ancho
  // pensado para hasta 4 botones (Emitir OT, Emitir Requerimiento, Historial,
  // Plataformas) — antes eran como mucho 3 y sobraba con 118.
  const stickyAccionesSx = {
    width: 148,
    position: "sticky",
    right: 0,
    zIndex: 2,
    bgcolor: (t) => (t.palette.mode === "dark" ? t.palette.background.paper : "#fff"),
    boxShadow: "-4px 0 6px -4px rgba(0,0,0,0.18)",
  };

  const renderPlataforma = (cli) => {
    if (cli.nombre_servicio !== "AVL") return <Typography variant="caption" color="text.secondary">—</Typography>;
    const plataforma = plataformaPorServicio[cli.cod_servicio_cliente];
    if (plataforma === undefined) return <Typography variant="caption" color="text.secondary">…</Typography>;
    if (!plataforma) return <Typography variant="caption" color="text.secondary">Sin plataforma</Typography>;
    const cfg = PLATAFORMA_LABEL[plataforma] || { label: plataforma, color: "default" };
    return <Chip size="small" color={cfg.color} label={cfg.label} sx={{ height: 18, fontSize: "0.65rem" }} />;
  };

  // Descripción del servicio completa (sin cortar) y, si lo tiene, el aviso de "guardar recorrido".
  const renderServicioDescripcion = (cli, mobile = false) => (
    <Box sx={{ minWidth: 0 }}>
      {cli.descripcion && (
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: mobile ? "0.8rem" : "0.74rem", lineHeight: 1.25, overflowWrap: "anywhere" }}>
          {cli.descripcion}
        </Typography>
      )}
      {cli.tiempo_conservacion_recorrido && (
        <Typography
          variant="caption"
          sx={{ display: "block", mt: 0.15, fontSize: "0.68rem", lineHeight: 1.2, fontWeight: 700, color: "#d81b60", overflowWrap: "anywhere" }}
        >
          Guardar recorrido: {cli.tiempo_conservacion_recorrido}
        </Typography>
      )}
    </Box>
  );

  const renderPagination = () => (
    <TablePagination
      component="div"
      count={total}
      page={page}
      rowsPerPage={rowsPerPage}
      rowsPerPageOptions={[10, 30, 50, 100]}
      labelRowsPerPage="Filas por pagina"
      labelDisplayedRows={({ from, to, count }) =>
        `${from}-${to} de ${count !== -1 ? count : `mas de ${to}`}`
      }
      onPageChange={(_, nextPage) => onPageChange?.(nextPage)}
      onRowsPerPageChange={(e) => onRowsPerPageChange?.(Number(e.target.value))}
      sx={{
        minHeight: 40,
        "& .MuiTablePagination-toolbar": { minHeight: 40, px: 1 },
        "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
          fontSize: "0.78rem",
        },
      }}
    />
  );

  const renderAcciones = (cli, isMobileView) => (
    <Box sx={{ display: "flex", gap: 0.25, alignItems: "center" }}>
      {isMobileView ? (
        <Button
          size="small"
          variant="contained"
          color="success"
          onClick={(e) => {
            e.stopPropagation();
            setClienteParaOT(cli);
            setOtDialogOpen(true);
          }}
          startIcon={<AddIcon />}
          sx={{ minHeight: 30, fontSize: "0.72rem" }}
        >
          Emitir OT
        </Button>
      ) : (
        <Tooltip title="Emitir Orden de Trabajo">
          <IconButton
            size="small"
            sx={{ color: "success.main" }}
            onClick={(e) => {
              e.stopPropagation();
              setClienteParaOT(cli);
              setOtDialogOpen(true);
            }}
          >
            <AddIcon fontSize="inherit" />
          </IconButton>
        </Tooltip>
      )}

      {cli.nombre_servicio === "AVL" &&
        (isMobileView ? (
          <Button
            size="small"
            variant="contained"
            color="primary"
            onClick={(e) => {
              e.stopPropagation();
              onEmitirReq(cli);
            }}
            startIcon={<EventIcon />}
            sx={{ minHeight: 30, fontSize: "0.72rem" }}
          >
            Emitir Requerimiento
          </Button>
        ) : (
          <Tooltip title="Emitir Requerimiento">
            <IconButton
              size="small"
              color="primary"
              onClick={(e) => {
                e.stopPropagation();
                onEmitirReq(cli);
              }}
            >
              <EventIcon fontSize="inherit" />
            </IconButton>
          </Tooltip>
        ))}

      <Tooltip title="Historial">
        <IconButton
          size="small"
          sx={{ color: "gray" }}
          onClick={(e) => {
            e.stopPropagation();
            onOpenHistorial?.(cli);
          }}
        >
          <HistoryIcon fontSize="inherit" />
        </IconButton>
      </Tooltip>

      {/* Solo tiene sentido para AVL (es un dato de módems/vehículos, igual que
          renderPlataforma más arriba). Abre la vista con las 3 plataformas
          (TRS Rastreo / Wialon / Leasecontrol) — no depende de que ya se haya
          resuelto una, porque desde ahí también se puede vincular/registrar. */}
      {cli.nombre_servicio === "AVL" && (
        <Tooltip title="Plataformas">
          <IconButton
            size="small"
            sx={{ color: "info.main" }}
            onClick={(e) => {
              e.stopPropagation();
              onOpenPlataformas?.(cli);
            }}
          >
            <SatelliteAltIcon fontSize="inherit" />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );

  const dialogoEmitirOT = clienteParaOT && (
    <FormularioOT
      open={otDialogOpen}
      onClose={() => {
        setOtDialogOpen(false);
        setClienteParaOT(null);
      }}
      cliente={clienteParaOT}
    />
  );

  if (isMobile) {
    return (
      <>
        <Grid container spacing={1}>
          {clientes.map((cli) => (
            <Grid size={{ xs: 12 }} key={cli.cod_servicio_cliente}>
              <Card variant="outlined">
                <CardContent sx={{ p: 1.25, "&:last-child": { pb: 1.25 } }}>
                  <Typography variant="subtitle1" component="div" sx={{ mb: 0.5, fontSize: "0.95rem", display: "flex", alignItems: "center", gap: 0.75 }}>
                    <ServicioAvatar imagen={cli.imagen_principal} nombre={cli.nombre_cliente} size={28} />
                    <Box component="span" sx={{ overflowWrap: "anywhere", lineHeight: 1.2 }}>{cli.nombre_cliente} ({cli.cod_servicio_cliente})</Box>
                  </Typography>

                  <Box mb={0.75}>
                    <Box sx={{ mb: 0.5 }}>{renderServicioDescripcion(cli, true)}</Box>
                    <Typography color="text.secondary" variant="body2">
                      Servicio: {cli.nombre_servicio}
                    </Typography>
                    {cli.nombre_servicio === "AVL" && (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.25 }}>
                        <Typography color="text.secondary" variant="body2">Plataforma:</Typography>
                        {renderPlataforma(cli)}
                      </Box>
                    )}
                    <Typography color="text.secondary" variant="body2">
                      Grupo: {cli.grupo || "-"}
                    </Typography>
                    <Typography color="text.secondary" variant="body2">
                      Nombre: {cli.nombre || "-"}
                    </Typography>
                    <Typography color="text.secondary" variant="body2">
                      Sucursal: {cli.sucursal_facturar}
                    </Typography>
                  </Box>

                  <Box sx={{ mt: 1, mb: 0.75 }}>
                    <Typography variant="caption" gutterBottom>
                      Equipos:
                    </Typography>
                    <EquiposChips equiposMap={equiposMap} codServicioCliente={cli.cod_servicio_cliente} />
                  </Box>

                  <Box sx={{ mt: 1, display: "flex", justifyContent: "flex-end" }}>
                    {renderAcciones(cli, true)}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
        {renderPagination()}
        {dialogoEmitirOT}
      </>
    );
  }

  return (
    <>
      <TableContainer component={Paper}>
        <Table
          size="small"
          sx={{
            tableLayout: "fixed",
            "& .MuiTableCell-root": {
              px: 0.75,
              py: 0.45,
              fontSize: "0.78rem",
              lineHeight: 1.25,
              verticalAlign: "top",
            },
            "& .MuiTableCell-head": {
              fontWeight: 700,
              whiteSpace: "nowrap",
            },
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 76 }}>Cod Cliente</TableCell>
              <TableCell sx={{ width: 58 }}>Cod</TableCell>
              <TableCell sx={{ width: 290 }}>Cliente</TableCell>
              <TableCell sx={{ width: 130 }}>Grupo</TableCell>
              <TableCell sx={{ width: 150 }}>Nombre</TableCell>
              <TableCell sx={{ width: 76 }}>Servicio</TableCell>
              <TableCell sx={{ width: 96 }}>Plataforma</TableCell>
              <TableCell sx={{ width: 96 }}>Sucursal</TableCell>
              <TableCell sx={{ width: 170 }}>Equipos</TableCell>
              <TableCell sx={stickyAccionesSx}>Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {clientes.map((cli) => (
              <TableRow key={cli.cod_servicio_cliente} hover>
                <TableCell>{cli.cod_cliente}</TableCell>
                <TableCell>{cli.cod_servicio_cliente}</TableCell>
                <TableCell>
                  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.75 }}>
                    <ServicioAvatar imagen={cli.imagen_principal} nombre={cli.nombre_cliente} />
                    <Box sx={{ minWidth: 0 }}>
                      <Box component="span" sx={{ display: "block", fontWeight: 600, overflowWrap: "anywhere" }}>{cli.nombre_cliente}</Box>
                      {renderServicioDescripcion(cli)}
                    </Box>
                  </Box>
                </TableCell>
                <TableCell sx={{ overflowWrap: "anywhere" }}>{cli.grupo || "-"}</TableCell>
                <TableCell sx={{ overflowWrap: "anywhere" }}>{cli.nombre || "-"}</TableCell>
                <TableCell>{cli.nombre_servicio}</TableCell>
                <TableCell>{renderPlataforma(cli)}</TableCell>
                <TableCell>{cli.sucursal_facturar}</TableCell>
                <TableCell>
                  <EquiposChips equiposMap={equiposMap} codServicioCliente={cli.cod_servicio_cliente} />
                </TableCell>
                <TableCell sx={{ ...stickyAccionesSx, whiteSpace: "nowrap" }}>{renderAcciones(cli, false)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {renderPagination()}
      {dialogoEmitirOT}
    </>
  );
}
